/**
 * QA Panel — Mini Backoffice controller.
 *
 * Lets QA edit lobby sections live: rename, reorder, add/remove games,
 * delete, reorder games within a section. All edits go through the
 * existing appStore.setSections / setSupportedGames actions — no
 * production code is touched.
 *
 * "Reset to API" calls the existing fetchSettings function and pushes the
 * fresh result back into the store, discarding any local edits.
 *
 * Special / well-known sections (those whose name is referenced literally
 * by lobby code, or that have special config like topLimit / jackpotName /
 * context) are flagged as locked. The popup disables their name field and
 * delete button so QA doesn't accidentally break the section's special UI.
 * Locked sections still allow game add / remove / reorder.
 */

import {
    FAVORITE_SECTION_NAME,
    HOT_AND_COLD_SECTION_NAME,
    LAST_PLAYED_SECTION_NAME,
    SECTION_NAMES,
} from '@repo/common';
import { useAppStore } from 'store/app/appStore';
import { useFeatureFlagsStore } from 'store/featureFlags/featureFlagsStore';
import { fetchSettings } from 'core/api/endpoints/settings.api';
import { isGameNew } from 'shared/utils/dateHelpers';
import { useTournamentCupStore } from '../../campaigns/store/tournamentCupStore';
import { BANNER_TAG, hasBannerTag, setBannerTag } from '../../games/constants/gameBanners';
import type ISectionSettings from '../../../types/models/ISectionSettings';
import type IGameSettings from '../../../types/models/IGameSettings';
import type { ApiGameSettings, BrandingType } from '../../../types/api/api.types';

/* ── Constants ──────────────────────────────────────────────────── */

/**
 * Hardcoded list of currencies offered by the QA Panel currency switcher.
 *
 * Why hardcoded: the backend `fetchSettings(apiHost, currency)` call only
 * returns bet ranges / denominations for the requested currency, and the
 * lobby's `addEGTCurrency` step pads a synthetic 'EGT' key onto every game.
 * That means the union of `minPossibleBets` keys at runtime is at most
 * `[playerCurrency, 'EGT']` — never enough to populate a real switcher.
 * QA wants to preview the lobby in any currency the platform might assign,
 * so we expose a fixed list independent of what's loaded.
 */
const QA_CURRENCY_OPTIONS = ['BGN', 'EUR', 'USD', 'GBP', 'RON', 'TRY', 'EGT'] as const;

/* ── Public state shape sent to the popup ───────────────────────── */

export interface BackofficeGameRef {
    gameKey: string;
    name: string;
    /* Flags — editable in the Games sub-tab. */
    topGame: boolean;
    isInTournament: boolean;
    newGame: boolean;
    gameFeatures: string[];
    jackpotName: string | null;
    displayJackpotName: string | null;
    /* Raw date fields from supportedGames. Used as the visual source of
     * truth for date inputs; when edited, they flow through isGameNew() to
     * re-derive the newGame flag. */
    activatedOn: string | null;
    addedToLobbyOn: string | null;
    /* Current active players from the CurrentPlayersSlice. Not part of
     * IGameSettings — lives in its own store slice. 0 when unset. */
    currentPlayers: number;
    /* Min/max bet for the currently-loaded currency, read from the
     * `minPossibleBets` / `maxPossibleBets` Maps on IGameSettings. Already
     * in display units (the settings.api parser divides by 100 when
     * building from betRanges). 0 when the game has no entry for the
     * current currency. */
    minBet: number;
    maxBet: number;
    /* The currency these min/max bet values correspond to. Empty string
     * when no currency is loaded yet (edge case — edit UI should disable
     * the bet inputs in that case). */
    currency: string;
}

/**
 * Patch payload for a game edit. Any subset of these fields may be sent;
 * missing fields are left untouched. Dates are raw strings — the controller
 * re-derives `newGame` via isGameNew() when either date changes.
 */
export interface GameEditPatch {
    topGame?: boolean;
    isInTournament?: boolean;
    gameFeatures?: string[];
    jackpotName?: string | null;
    displayJackpotName?: string | null;
    activatedOn?: string | null;
    addedToLobbyOn?: string | null;
    currentPlayers?: number;
    /* Bet overrides apply to the currently-loaded currency only
     * (read from appStore.balance?.currency inside updateGame). */
    minBet?: number;
    maxBet?: number;
}

export interface BackofficeSection {
    name: string;
    order: number;
    isLocked: boolean;
    lockReason: string | null;
    games: BackofficeGameRef[];
}

export interface BackofficeState {
    /** Sections in their current store order. */
    sections: BackofficeSection[];
    /** All games the lobby knows about — for the "add game" picker. */
    availableGames: BackofficeGameRef[];
    /** True if the controller is currently waiting on a Reset-to-API fetch. */
    isResetting: boolean;
    /** Last error message from a refetch attempt, or null. */
    resetError: string | null;
    /** Current lobby open type — 'fullscreen' or 'ingame'. */
    openType: 'fullscreen' | 'ingame';
    /** Current seasonal branding feature flag value, or null when unset. */
    seasonalBranding: string | null;
    /** True when 'noJackpot' is present in settings.restrictions — swaps the
     *  lobby vocabulary from "Jackpot" to "Bonus" (affects section/tab labels,
     *  help folder path, filter labels, linked-jackpot display). */
    noJackpot: boolean;
    /** Currently-selected balance currency (e.g. 'EUR', 'USD'). Empty string
     *  when no balance is loaded yet. Drives formatted balance, formatted
     *  prizes, and the per-currency bet ranges shown in game cards. */
    currency: string;
    /** Hardcoded list of QA-selectable currencies (see QA_CURRENCY_OPTIONS),
     *  with the current `balance.currency` appended when it isn't already in
     *  the list. Source of truth for the Casino → Currency dropdown. */
    availableCurrencies: string[];
    /** True when a mocked HOT_AND_COLD section is currently present in
     *  `appStore.sections`. Derived live in `getState()` by checking the
     *  section list — there is no persisted "mock active" flag because this
     *  is a transient debug button (the section either exists or it doesn't). */
    hotColdMockActive: boolean;
    /** Campaign feature flags — mirror of featureFlagsStore.flags. Gate
     *  whether the PrizePop / Tournaments / Rebate campaign sections render
     *  at all (see features/campaigns/components/CampaignSectionDispatcher). */
    enablePrizePopCampaign: boolean;
    enableTournamentsCampaign: boolean;
    enableRebateCampaign: boolean;
}

/** The three campaign feature flags editable from the Casino sub-tab. */
export type CampaignFeatureFlag = 'enablePrizePopCampaign' | 'enableTournamentsCampaign' | 'enableRebateCampaign';

/* ── Locked-section detection ───────────────────────────────────── */

/**
 * The only sections that refuse rename / delete. Lobby code has literal
 * name-based logic for these (favorites tab, last-played carousel, top
 * games ribbon), so renaming them would silently break the UI.
 *
 * Every other section — including TOP_GAMES, PROGRESSIVE, TOURNAMENTS, and
 * anything else — is editable. QA can rename them, delete them, or add and
 * remove games at will. If a rename breaks a special UI, that's the point
 * of the QA test: find the breakage before release.
 */
const LOCKED_SECTION_NAMES = new Set<string>([
    LAST_PLAYED_SECTION_NAME as string,
    FAVORITE_SECTION_NAME as string,
    SECTION_NAMES.TOP as string,
]);

interface LockResult {
    locked: boolean;
    reason: string | null;
}

function detectLock(section: ISectionSettings): LockResult {
    if (LOCKED_SECTION_NAMES.has(section.name as string)) {
        return {
            locked: true,
            reason: 'Special section — lobby code looks it up by literal name',
        };
    }
    return { locked: false, reason: null };
}

/* ── Controller ─────────────────────────────────────────────────── */

/**
 * Snapshot of the four QA-editable Casino-tab fields as they were at lobby
 * boot, captured once `settingsRequestDone === true` AND `balance` is loaded.
 * Used by `resetToApi` to restore not just sections/supportedGames but every
 * Casino-tab override the user may have applied. Stashed exactly once per
 * session — subsequent Reset clicks restore the original boot values, never
 * a more recent QA-set value.
 */
interface ApiOriginals {
    openType?: 'fullscreen' | 'ingame';
    seasonalBranding?: BrandingType | null;
    noJackpot?: boolean;
    currency?: string;
}

export class BackofficeController {
    private onChange: (() => void) | null = null;
    private storeUnsubscribe: (() => void) | null = null;
    private featureFlagsUnsubscribe: (() => void) | null = null;
    private originalsStashUnsubscribe: (() => void) | null = null;
    private isResetting = false;
    private resetError: string | null = null;
    private apiOriginals: ApiOriginals | null = null;

    init(onChange: () => void): void {
        this.onChange = onChange;
        // Re-broadcast state whenever the store sections change. Same pattern
        // as the jackpot mock controller.
        this.storeUnsubscribe = useAppStore.subscribe((state, prev) => {
            if (
                state.sections !== prev.sections ||
                state.supportedGames !== prev.supportedGames ||
                state.currentPlayers !== prev.currentPlayers ||
                state.settings.openType !== prev.settings.openType ||
                state.settings.restrictions !== prev.settings.restrictions ||
                state.balance !== prev.balance
            ) {
                this.onChange?.();
            }
        });

        // Also watch the separate featureFlagsStore for seasonalBranding and
        // campaign-flag changes.
        this.featureFlagsUnsubscribe = useFeatureFlagsStore.subscribe((state, prev) => {
            if (
                state.flags.seasonalBranding !== prev.flags.seasonalBranding ||
                state.flags.enablePrizePopCampaign !== prev.flags.enablePrizePopCampaign ||
                state.flags.enableTournamentsCampaign !== prev.flags.enableTournamentsCampaign ||
                state.flags.enableRebateCampaign !== prev.flags.enableRebateCampaign
            ) {
                this.onChange?.();
            }
        });

        this.armOriginalsStash();
    }

    destroy(): void {
        this.storeUnsubscribe?.();
        this.storeUnsubscribe = null;
        this.featureFlagsUnsubscribe?.();
        this.featureFlagsUnsubscribe = null;
        this.originalsStashUnsubscribe?.();
        this.originalsStashUnsubscribe = null;
        this.onChange = null;
    }

    /* ── API-original stash (boot snapshot for Reset-to-API) ───── */

    /**
     * Capture the boot-time values of the four Casino-tab fields exactly
     * once. Reads from appStore (`settings.openType`, `settings.restrictions`,
     * `balance.currency`) and featureFlagsStore (`flags.seasonalBranding`)
     * the same way `getState()` already does, so the stash matches what
     * Reset-to-API needs to push back through public store actions.
     *
     * If lobby boot is already past `settingsRequestDone` and `balance` is
     * loaded, capture immediately. Otherwise subscribe and capture once both
     * conditions hold, then unsubscribe — we only ever stash once per session.
     */
    private armOriginalsStash(): void {
        if (this.apiOriginals) return;

        const tryStash = (): boolean => {
            const state = useAppStore.getState();
            if (!state.settingsRequestDone) return false;
            if (!state.balance) return false;
            this.apiOriginals = {
                openType: state.settings.openType,
                seasonalBranding: useFeatureFlagsStore.getState().flags.seasonalBranding,
                noJackpot: (state.settings.restrictions ?? []).includes('noJackpot'),
                currency: state.balance.currency,
            };
            return true;
        };

        if (tryStash()) return;

        this.originalsStashUnsubscribe = useAppStore.subscribe(() => {
            if (this.apiOriginals) return;
            if (tryStash()) {
                this.originalsStashUnsubscribe?.();
                this.originalsStashUnsubscribe = null;
            }
        });
    }

    /* ── Snapshot generation ───────────────────────────────────── */

    getState(): BackofficeState {
        const store = useAppStore.getState();
        const rawGames = store.supportedGames as Record<string, { activatedOn?: string; addedToLobbyOn?: string; name?: string }>;

        // Read the live tournament-cup set so the form reflects what's actually
        // visible in the lobby (the `isInTournament` field on IGameSettings
        // isn't consumed by any render code — only tournamentCupStore is).
        const cupSet = useTournamentCupStore.getState().gamesWithCups;

        // Read the player's currency once per snapshot — the bet fields are
        // scoped to this currency. Empty string when no balance is loaded yet.
        const currency = store.balance?.currency ?? '';

        const buildGameRef = (g: IGameSettings): BackofficeGameRef => {
            const raw = rawGames[g.gameKey] ?? undefined;
            return {
                gameKey: g.gameKey,
                name: g.name || g.gameKey,
                topGame: hasBannerTag(g.gameBanners, BANNER_TAG.TOP),
                isInTournament: cupSet.has(g.gameKey),
                newGame: hasBannerTag(g.gameBanners, BANNER_TAG.NEW),
                gameFeatures: Array.isArray(g.gameFeatures) ? [...g.gameFeatures] : [],
                jackpotName: g.jackpotName ?? null,
                displayJackpotName: g.displayJackpotName ?? null,
                activatedOn: raw?.activatedOn ?? null,
                addedToLobbyOn: raw?.addedToLobbyOn ?? null,
                currentPlayers: store.getPlayerCountForGame(g.gameKey),
                minBet: currency ? g.minPossibleBets.get(currency) ?? 0 : 0,
                maxBet: currency ? g.maxPossibleBets.get(currency) ?? 0 : 0,
                currency,
            };
        };

        const sections: BackofficeSection[] = store.sections.map((s, idx) => {
            const lock = detectLock(s);
            return {
                name: s.name as string,
                order: idx,
                isLocked: lock.locked,
                lockReason: lock.reason,
                games: (s.games || []).map(buildGameRef),
            };
        });

        // availableGames is used for the "add game to section" picker — it
        // only needs gameKey + name. Pad the other fields with defaults so
        // the type stays consistent across all game refs in the payload.
        const availableGames: BackofficeGameRef[] = Object.entries(rawGames || {})
            .map(([gameKey, raw]) => ({
                gameKey,
                name: raw?.name || gameKey,
                topGame: false,
                isInTournament: false,
                newGame: false,
                gameFeatures: [],
                jackpotName: null,
                displayJackpotName: null,
                activatedOn: raw?.activatedOn ?? null,
                addedToLobbyOn: raw?.addedToLobbyOn ?? null,
                currentPlayers: 0,
                minBet: 0,
                maxBet: 0,
                currency,
            }))
            .sort((a, b) => a.name.localeCompare(b.name));

        // The QA currency switcher uses a hardcoded list (see
        // QA_CURRENCY_OPTIONS for the rationale). Append the current balance
        // currency when the platform has assigned something outside the list,
        // so the active selection is still visible / selectable.
        const availableCurrencies: string[] = [...QA_CURRENCY_OPTIONS];
        if (currency && !availableCurrencies.includes(currency)) {
            availableCurrencies.push(currency);
        }

        const hotColdMockActive = store.sections.some(
            (s) => s.name === HOT_AND_COLD_SECTION_NAME
        );

        const flags = useFeatureFlagsStore.getState().flags;

        return {
            sections,
            availableGames,
            isResetting: this.isResetting,
            resetError: this.resetError,
            openType: store.settings.openType || 'fullscreen',
            seasonalBranding: flags.seasonalBranding ?? null,
            noJackpot: (store.settings.restrictions ?? []).includes('noJackpot'),
            currency,
            availableCurrencies,
            hotColdMockActive,
            enablePrizePopCampaign: flags.enablePrizePopCampaign,
            enableTournamentsCampaign: flags.enableTournamentsCampaign,
            enableRebateCampaign: flags.enableRebateCampaign,
        };
    }

    /* ── Campaign feature flags (Casino sub-tab) ───────────────── */

    /** Flip one campaign feature flag at runtime, through the same
     *  featureFlagsStore.setFlags path the rest of the store uses. */
    setCampaignFlag(flag: CampaignFeatureFlag, value: boolean): void {
        const flagsStore = useFeatureFlagsStore.getState();
        flagsStore.setFlags({ ...flagsStore.flags, [flag]: value });
    }

    /* ── Currency switcher (Casino sub-tab) ────────────────────── */

    /**
     * Flip the player's currency at runtime. Lets QA preview formatted
     * balance, formatted prizes, and per-currency bet ranges for any
     * currency the lobby has data for. No-ops with a console warning when
     * `balance` isn't loaded yet — the lobby has no currency to swap on.
     */
    setCurrency(nextCurrency: string): void {
        if (!nextCurrency) return;
        const current = useAppStore.getState().balance;
        if (!current) {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] Cannot set currency — appStore.balance is not loaded yet.');
            return;
        }
        if (current.currency === nextCurrency) return;
        useAppStore.getState().updateBalance({
            balance: current.balance,
            units: current.units,
            currency: nextCurrency,
        });
    }

    /* ── Section operations ────────────────────────────────────── */

    renameSection(oldName: string, newName: string): void {
        const trimmed = newName.trim();
        if (!trimmed) return;
        if (trimmed === oldName) return;
        const store = useAppStore.getState();
        const sections = store.sections;
        const target = sections.find((s) => s.name === oldName);
        if (!target) return;
        // Refuse if the section is locked.
        if (detectLock(target).locked) return;
        // Refuse if the new name collides with another section.
        if (sections.some((s) => s.name === trimmed)) return;

        const next = sections.map((s) =>
            s.name === oldName ? ({ ...s, name: trimmed } as ISectionSettings) : s
        );
        store.setSections(next);
    }

    /**
     * Replace the current section order with an explicit permutation. Used by
     * the popup's drag-and-drop reorder UI. Defensive: silently no-ops with a
     * console warning when `names` is not a permutation of the current section
     * set (e.g. popup dispatched a stale order after a mid-drag state change
     * was force-cancelled). Better to lose the gesture than to corrupt the
     * section list.
     */
    setSectionOrder(names: string[]): void {
        const store = useAppStore.getState();
        const current = store.sections;
        if (names.length !== current.length) {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] setSectionOrder rejected — length mismatch.');
            return;
        }
        const currentSet = new Set(current.map((s) => s.name as string));
        const incomingSet = new Set(names);
        if (incomingSet.size !== names.length) {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] setSectionOrder rejected — duplicate names.');
            return;
        }
        for (const n of names) {
            if (!currentSet.has(n)) {
                // eslint-disable-next-line no-console
                console.warn('[QA Panel] setSectionOrder rejected — unknown section', n);
                return;
            }
        }
        const byName = new Map(current.map((s) => [s.name as string, s]));
        const next: ISectionSettings[] = names.map((n) => byName.get(n) as ISectionSettings);
        store.setSections(next);
    }

    deleteSection(name: string): void {
        const store = useAppStore.getState();
        const target = store.sections.find((s) => s.name === name);
        if (!target) return;
        if (detectLock(target).locked) return;
        store.setSections(store.sections.filter((s) => s.name !== name));
    }

    /* ── In-section game operations ────────────────────────────── */

    addGameToSection(sectionName: string, gameKey: string): void {
        const store = useAppStore.getState();

        // Skip if the target section doesn't exist, or if the game is already there.
        const target = store.sections.find((s) => s.name === sectionName);
        if (!target) return;
        if ((target.games || []).some((g) => g.gameKey === gameKey)) return;

        // `supportedGames` holds the RAW ApiGameSettings shape — denominations
        // are plain objects, not Maps. The lobby's render code (e.g.
        // getGamesMinDenomination in @repo/common) expects the fully-PROCESSED
        // IGameSettings shape with Map instances for denominations,
        // minPossibleBets, maxPossibleBets, etc. Pushing a raw game into a
        // section would crash when those helpers hit the Map-assuming code.
        //
        // The transformation logic lives inline inside settings.api.ts's
        // parseSettingsResponse — extracting it here would mean duplicating
        // ~30 lines of the API layer. Instead, find a "donor": the same game
        // as it already appears in any existing section. It's already in the
        // processed shape, so we can clone it and drop it into the new section
        // unchanged. Every field is either identical across sections or
        // computed globally (topGame, newGame), so shallow-cloning is safe.
        //
        // If no donor exists (game is in supportedGames but not in any
        // currently-loaded section — rare edge case when QA has emptied every
        // section containing it), skip with a console warning. "Reset to API"
        // will restore the full state.
        let donor: IGameSettings | null = null;
        for (const s of store.sections) {
            const found = (s.games || []).find((g) => g.gameKey === gameKey);
            if (found) {
                donor = found;
                break;
            }
        }
        if (!donor) {
            // eslint-disable-next-line no-console
            console.warn(
                '[QA Panel] Cannot add',
                gameKey,
                'to',
                sectionName,
                '— game is not present in any other section to clone from. Click "Reset to API" to restore the full data.'
            );
            return;
        }

        const cloned: IGameSettings = { ...donor };
        const next = store.sections.map((s) => {
            if (s.name !== sectionName) return s;
            return { ...s, games: [...(s.games || []), cloned] } as ISectionSettings;
        });
        store.setSections(next);
    }

    removeGameFromSection(sectionName: string, gameKey: string): void {
        const store = useAppStore.getState();
        const next = store.sections.map((s) => {
            if (s.name !== sectionName) return s;
            return {
                ...s,
                games: (s.games || []).filter((g) => g.gameKey !== gameKey),
            } as ISectionSettings;
        });
        store.setSections(next);
    }

    /**
     * Patch a single game across every section it appears in. All edits
     * go through the appStore's setSections action so React components
     * re-render naturally.
     *
     * Date fields (activatedOn / addedToLobbyOn) are special: the raw
     * game object in `supportedGames` carries them, but the runtime
     * IGameSettings in sections only carries the derived `newGame`
     * boolean. When either date is sent in the patch, this method:
     *   1. Updates supportedGames so the raw value persists.
     *   2. Recomputes `newGame` via isGameNew() using the new date(s) and
     *      whatever the other date currently is.
     *   3. Writes the derived newGame onto every in-section instance of
     *      this game.
     * The dates themselves aren't stored on IGameSettings — if anything
     * reads them at render time later, the raw entry in supportedGames is
     * the source of truth.
     */
    updateGame(gameKey: string, patch: GameEditPatch): void {
        const store = useAppStore.getState();
        const rawGames = store.supportedGames;
        const rawGame: ApiGameSettings | undefined = rawGames[gameKey];

        // If either date is being patched, update supportedGames and
        // pre-compute the derived newGame flag once.
        let derivedNewGame: boolean | undefined;
        if ('activatedOn' in patch || 'addedToLobbyOn' in patch) {
            const nextActivated = 'activatedOn' in patch ? patch.activatedOn : rawGame?.activatedOn ?? null;
            const nextAdded = 'addedToLobbyOn' in patch ? patch.addedToLobbyOn : rawGame?.addedToLobbyOn ?? null;
            derivedNewGame = isGameNew(nextActivated ?? undefined, nextAdded ?? undefined);

            // Persist the raw dates so the next getState snapshot shows them
            // in the form, and so future date patches can read the "other" date.
            // A cleared date is stored as '' — every reader treats the raw
            // dates by truthiness, so '' behaves exactly like "no date".
            if (rawGame) {
                const nextSupported = {
                    ...rawGames,
                    [gameKey]: { ...rawGame, activatedOn: nextActivated ?? '', addedToLobbyOn: nextAdded ?? '' },
                };
                store.setSupportedGames(nextSupported);
            }
        }

        // Bet overrides apply to the currently-loaded currency only. Read
        // once here so every touched game writes to the same key, and skip
        // the bet patch entirely when no balance/currency is loaded yet.
        const hasBetPatch = patch.minBet !== undefined || patch.maxBet !== undefined;
        let betCurrency = '';
        if (hasBetPatch) {
            betCurrency = store.balance?.currency ?? '';
            if (!betCurrency) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[QA Panel] Cannot apply bet patch to',
                    gameKey,
                    '— no currency loaded in appStore.balance. Skipping bet fields.'
                );
            }
        }
        const applyBets = hasBetPatch && betCurrency !== '';

        // Walk every section, patch any matching game in place. The spread
        // produces a new game reference, which the (single, unified) Section
        // and Game memo comparators pick up via reference equality on `data`.
        // Min/max bets require cloning the underlying Maps — mutating them in
        // place would not change the Map reference and wouldn't update
        // MinMaxBet.tsx reliably. `new Map(oldMap)` then `.set(currency, n)`.
        const next = store.sections.map((s) => {
            let touched = false;
            const newGames = (s.games || []).map((g) => {
                if (g.gameKey !== gameKey) return g;
                touched = true;
                const updated: IGameSettings = { ...g };
                if (patch.topGame !== undefined) updated.gameBanners = setBannerTag(updated.gameBanners, BANNER_TAG.TOP, patch.topGame);
                if (patch.isInTournament !== undefined) updated.isInTournament = patch.isInTournament;
                if (patch.gameFeatures !== undefined) updated.gameFeatures = [...patch.gameFeatures];
                if (patch.jackpotName !== undefined) updated.jackpotName = patch.jackpotName;
                if (patch.displayJackpotName !== undefined) updated.displayJackpotName = patch.displayJackpotName;
                if (derivedNewGame !== undefined) updated.gameBanners = setBannerTag(updated.gameBanners, BANNER_TAG.NEW, derivedNewGame);
                if (applyBets && patch.minBet !== undefined) {
                    const nextMin = new Map(g.minPossibleBets);
                    nextMin.set(betCurrency, patch.minBet);
                    updated.minPossibleBets = nextMin;
                }
                if (applyBets && patch.maxBet !== undefined) {
                    const nextMax = new Map(g.maxPossibleBets);
                    nextMax.set(betCurrency, patch.maxBet);
                    updated.maxPossibleBets = nextMax;
                }
                return updated;
            });
            return touched ? ({ ...s, games: newGames } as ISectionSettings) : s;
        });
        store.setSections(next);

        // `currentPlayers` lives in its own Zustand slice (CurrentPlayersSlice),
        // not on IGameSettings. Route it through the slice setter so the
        // usePlayerCountForGame hook that drives <CurrentPlayersCount/> fires.
        if (patch.currentPlayers !== undefined) {
            useAppStore.getState().setPlayerCountForGame(gameKey, patch.currentPlayers);
        }

        // `isInTournament` on IGameSettings is read by the tournaments feature's
        // filter logic but NOT by any game card render code. The visible
        // tournament cup badge is driven by a separate `tournamentCupStore` —
        // `useGameCupStatus(gameKey)` checks whether the gameKey is in that
        // store's `gamesWithCups` Set. So to make the toggle actually visible,
        // we also update the cup store alongside the field patch.
        if (patch.isInTournament !== undefined) {
            const cupStore = useTournamentCupStore.getState();
            const currentCups = cupStore.gamesWithCups;
            const nextKeys = new Set(currentCups);
            if (patch.isInTournament) {
                nextKeys.add(gameKey);
            } else {
                nextKeys.delete(gameKey);
            }
            cupStore.setGamesWithCups(Array.from(nextKeys));
        }
    }

    /**
     * Replace the order of games inside a single section with an explicit
     * permutation. Used by the popup's drag-and-drop reorder UI. Defensive:
     * silently no-ops with a console warning when `gameKeys` is not a
     * permutation of the section's current game set (mid-drag cancel race).
     */
    setGameOrder(sectionName: string, gameKeys: string[]): void {
        const store = useAppStore.getState();
        const target = store.sections.find((s) => s.name === sectionName);
        if (!target) {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] setGameOrder rejected — unknown section', sectionName);
            return;
        }
        const currentGames = target.games || [];
        if (gameKeys.length !== currentGames.length) {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] setGameOrder rejected — length mismatch.');
            return;
        }
        const currentSet = new Set(currentGames.map((g) => g.gameKey));
        const incomingSet = new Set(gameKeys);
        if (incomingSet.size !== gameKeys.length) {
            // eslint-disable-next-line no-console
            console.warn('[QA Panel] setGameOrder rejected — duplicate gameKeys.');
            return;
        }
        for (const k of gameKeys) {
            if (!currentSet.has(k)) {
                // eslint-disable-next-line no-console
                console.warn('[QA Panel] setGameOrder rejected — unknown gameKey', k);
                return;
            }
        }
        const byKey = new Map(currentGames.map((g) => [g.gameKey, g]));
        const nextGames: IGameSettings[] = gameKeys.map((k) => byKey.get(k) as IGameSettings);
        const next = store.sections.map((s) =>
            s.name === sectionName ? ({ ...s, games: nextGames } as ISectionSettings) : s
        );
        store.setSections(next);
    }

    /* ── Hot/Cold mock injection ───────────────────────────────── */

    /**
     * Inject or remove a synthetic HOT_AND_COLD section so QA can see the
     * merged hot/cold UI without a populated BE response. The real init path
     * in `features/hotCold` runs once at lobby boot — by the time this button
     * fires, the source HOT_GAMES / COLD_GAMES sections have already been
     * stripped (dev/localhost responses are always empty), so going through
     * `createHotColdSection` isn't useful here. We synthesise the section
     * shape inline using donor games already present in `sections`.
     *
     * NOTE: This is intentionally transient — `setHotColdMock` is NOT
     * round-tripped through the preset system. After Reset-to-API (which
     * re-fetches settings and re-runs the real hot/cold pipeline), the mock
     * section vanishes naturally. Re-click the toggle if you want it back.
     */
    setHotColdMock(enabled: boolean): void {
        const store = useAppStore.getState();
        const existingMockIdx = store.sections.findIndex(
            (s) => s.name === HOT_AND_COLD_SECTION_NAME
        );

        if (!enabled) {
            if (existingMockIdx === -1) return;
            store.setSections(
                store.sections.filter((s) => s.name !== HOT_AND_COLD_SECTION_NAME)
            );
            return;
        }

        if (existingMockIdx !== -1) return;

        // Build a deduped pool of donor IGameSettings — games that already
        // live in some section. Mirrors the donor strategy used by both
        // `addGameToSection` above and `createHotColdSection` in features/hotCold.
        const donorByKey = new Map<string, IGameSettings>();
        for (const section of store.sections) {
            for (const g of section.games || []) {
                if (!donorByKey.has(g.gameKey)) donorByKey.set(g.gameKey, g);
            }
        }
        const donors = Array.from(donorByKey.values());
        if (donors.length < 2) {
            // eslint-disable-next-line no-console
            console.warn(
                '[QA Panel] Cannot mock HOT_AND_COLD — need at least 2 donor games across all sections.'
            );
            return;
        }

        // Pick up to 6 hot + 6 cold donors with distinct keys for visual
        // contrast. Take from opposite ends of the donor list so the two
        // baskets don't overlap when donors.length >= 2.
        const VISUAL_LIMIT = 6;
        const hotCount = Math.min(VISUAL_LIMIT, Math.floor(donors.length / 2));
        const coldCount = Math.min(VISUAL_LIMIT, donors.length - hotCount);

        // Fixed RTP values — descending for hot, ascending for cold. The
        // hot/cold endpoint emits already-sorted lists, so we mirror that.
        const HOT_RTPS = [95.5, 92.3, 90.1, 88.7, 86.2, 84.0];
        const COLD_RTPS = [0.0, 12.1, 15.3, 18.0, 20.2, 22.4];

        const hotGames: IGameSettings[] = donors.slice(0, hotCount).map((donor, i) => ({
            // New object reference — Game.tsx uses reference-equality memo.
            // The hot/cold banner tag marks the card as a Hot&Cold clone.
            ...donor,
            statisticRtp: HOT_RTPS[i] ?? HOT_RTPS[HOT_RTPS.length - 1],
            gameBanners: setBannerTag(donor.gameBanners, BANNER_TAG.HOT, true),
        }));
        const coldGames: IGameSettings[] = donors
            .slice(donors.length - coldCount)
            .map((donor, i) => ({
                ...donor,
                statisticRtp: COLD_RTPS[i] ?? COLD_RTPS[COLD_RTPS.length - 1],
                gameBanners: setBannerTag(donor.gameBanners, BANNER_TAG.COLD, true),
            }));

        // Template the section off any current section so gridBlocks / banner
        // defaults match what the lobby already renders. donors.length >= 2
        // guarantees sections is non-empty, but TS can't infer that — fall
        // back to a minimal hard-coded shape if the array is somehow empty.
        const template: ISectionSettings | undefined = store.sections[0];
        const mockSection: ISectionSettings = template
            ? {
                  ...template,
                  name: HOT_AND_COLD_SECTION_NAME,
                  jackpotName: null,
                  icon: 'hot_and_cold_icon',
                  games: hotGames,
                  hotColdSettings: {
                      hotGames,
                      coldGames,
                      backgroundSourceName: SECTION_NAMES.HOT_GAMES,
                  },
              }
            : {
                  name: HOT_AND_COLD_SECTION_NAME,
                  jackpotName: null,
                  icon: 'hot_and_cold_icon',
                  games: hotGames,
                  gridBlocks: 4,
                  banner: false,
                  hotColdSettings: {
                      hotGames,
                      coldGames,
                      backgroundSourceName: SECTION_NAMES.HOT_GAMES,
                  },
              };

        // Insert at position 1 when LAST_PLAYED occupies index 0, otherwise
        // at index 0 — matches the visible placement other carousels use.
        const next = [...store.sections];
        const lastPlayedAtTop = next[0]?.name === LAST_PLAYED_SECTION_NAME;
        next.splice(lastPlayedAtTop ? 1 : 0, 0, mockSection);
        store.setSections(next);
    }

    /* ── Reset to API ──────────────────────────────────────────── */

    /**
     * Refetch settings from the API and push the fresh result into the
     * store, discarding any local edits. Asynchronous — broadcasts an
     * "isResetting" state on start so the popup can show a spinner.
     *
     * After the refetch, restores the four Casino-tab fields
     * (`openType`, `seasonalBranding`, `noJackpot`, `currency`) from the
     * boot-time stash so Reset truly returns the lobby to its API-original
     * shape. Restoration MUST run AFTER `setSections` / `setSupportedGames`
     * so the refetch's own pushes don't overwrite the restored values.
     */
    async resetToApi(): Promise<void> {
        if (this.isResetting) return;
        this.isResetting = true;
        this.resetError = null;
        this.onChange?.();

        try {
            const state = useAppStore.getState();
            const apiHost = state.settings.apiHost;
            const currency = state.balance?.currency;
            if (!apiHost || !currency) {
                throw new Error('Missing apiHost or currency in appStore');
            }
            const fresh = await fetchSettings(apiHost, currency);
            const store = useAppStore.getState();
            store.setSections(fresh.sections);
            store.setSupportedGames(fresh.supportedGames);

            this.applyApiOriginals();
        } catch (err) {
            this.resetError = err instanceof Error ? err.message : String(err);
        } finally {
            this.isResetting = false;
            this.onChange?.();
        }
    }

    /**
     * Push the stashed boot-time values for the four Casino-tab fields back
     * through the same public store actions the popup uses. No-op when the
     * stash hasn't been captured yet (lobby was reset before settings ever
     * loaded — extremely unlikely but defended against).
     *
     * Order of operations matters. This MUST run after `setSections` /
     * `setSupportedGames` in `resetToApi`, since `updateSettings({ openType })`
     * and the `restrictions` patch both flow through the same `settings`
     * object — running them before the refetch's settings pushes would let
     * the later push silently overwrite the restored values.
     */
    private applyApiOriginals(): void {
        const originals = this.apiOriginals;
        if (!originals) return;

        const store = useAppStore.getState();

        if (originals.openType !== undefined) {
            store.updateSettings({ openType: originals.openType });
        }

        if (originals.seasonalBranding !== undefined) {
            // Match the bridge's setSeasonalBranding handler: truthy value flows
            // through the dedicated setter (which lower-cases), null clears via
            // setFlags spread (since setSeasonalBranding's signature is non-null).
            const flagsStore = useFeatureFlagsStore.getState();
            if (originals.seasonalBranding) {
                flagsStore.setSeasonalBranding(originals.seasonalBranding);
            } else {
                flagsStore.setFlags({ ...flagsStore.flags, seasonalBranding: null });
            }
        }

        if (originals.noJackpot !== undefined) {
            // Match the bridge's setNoJackpot handler: filter the entry out,
            // then append when the original was true. Preserves any other
            // restrictions the host platform may have set.
            const current = useAppStore.getState().settings.restrictions ?? [];
            const filtered = current.filter((r) => r !== 'noJackpot');
            const next = originals.noJackpot ? [...filtered, 'noJackpot'] : filtered;
            useAppStore.getState().updateSettings({ restrictions: next });
        }

        if (originals.currency !== undefined) {
            const currentBalance = useAppStore.getState().balance;
            if (!currentBalance) {
                // eslint-disable-next-line no-console
                console.warn('[QA Panel] Cannot restore currency — appStore.balance is not loaded.');
            } else if (currentBalance.currency !== originals.currency) {
                useAppStore.getState().updateBalance({
                    balance: currentBalance.balance,
                    units: currentBalance.units,
                    currency: originals.currency,
                });
            }
        }
    }
}
